<template>
  <div id="pagePartnerGroupUsers">
    <v-container fluid>
      <v-row>
        <v-col>
          <v-card class="elevation-2">
            <!-- Header -->
            <v-toolbar color="info" dark flat>
              <v-btn icon @click="goBack">
                <v-icon>mdi-arrow-left</v-icon>
              </v-btn>
              <v-icon class="mr-2">mdi-account-group</v-icon>
              <v-toolbar-title v-if="!$vuetify.breakpoint.mdAndDown">
                Usuarios del Grupo: {{ groupName }}
              </v-toolbar-title>
              <v-spacer></v-spacer>
              
              <!-- Búsqueda para escritorio -->
              <v-col cols="3" class="mr-3" v-if="!$vuetify.breakpoint.mdAndDown">
                <v-text-field
                  v-model="search"
                  label="Buscar usuario..."
                  prepend-inner-icon="mdi-magnify"
                  hide-details
                  clearable
                  rounded
                  single-line
                  filled
                  dense
                  dark
                ></v-text-field>
              </v-col>
              
              <!-- Menú de filtros -->
              <filter-menu
                title="Filtrar usuarios"
                :current-filter="filter"
                @canceled="resetFilters"
                @filtered="applyFilters"
              >
                <v-row>
                  <v-col cols="12">
                    <v-select
                      v-model="filter.roles"
                      :items="availableRoles"
                      item-text="name"
                      item-value="value"
                      label="Filtrar por rol"
                      outlined
                      clearable
                      hide-details="auto"
                      prepend-inner-icon="mdi-account-star"
                    >
                      <template v-slot:selection="{ item }">
                        <v-icon left small>mdi-shield-account</v-icon>
                        {{ item.name }}
                      </template>
                      <template v-slot:item="{ item }">
                        <v-list-item-icon>
                          <v-icon>mdi-shield-account</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title>{{ item.name }}</v-list-item-title>
                        </v-list-item-content>
                      </template>
                    </v-select>
                  </v-col>
                  <v-col cols="12">
                    <v-select
                      v-model="filter.status"
                      :items="estados"
                      item-text="name"
                      item-value="value"
                      label="Estado"
                      outlined
                      clearable
                      hide-details="auto"
                      prepend-inner-icon="mdi-checkbox-marked-circle-outline"
                    >
                      <template v-slot:selection="{ item }">
                        <v-icon left small :color="item.value === 10 ? 'green' : 'red'">
                          {{ item.value === 10 ? 'mdi-account-check' : 'mdi-account-off' }}
                        </v-icon>
                        {{ item.name }}
                      </template>
                      <template v-slot:item="{ item }">
                        <v-icon left :color="item.value === 10 ? 'green' : 'red'">
                          {{ item.value === 10 ? 'mdi-account-check' : 'mdi-account-off' }}
                        </v-icon>
                        {{ item.name }}
                      </template>
                    </v-select>
                  </v-col>
                  <v-col cols="12">
                    <v-autocomplete
                      v-model="filter.dealership_id"
                      :items="groupDealerships"
                      item-text="name"
                      item-value="id"
                      label="Filtrar por dealer"
                      outlined
                      clearable
                      hide-details="auto"
                      prepend-inner-icon="mdi-store"
                    >
                      <template v-slot:selection="{ item }">
                        <v-icon left small>mdi-store</v-icon>
                        {{ item.name }}
                      </template>
                      <template v-slot:item="{ item }">
                        <v-list-item-icon>
                          <v-icon>mdi-store</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title>{{ item.name }}</v-list-item-title>
                        </v-list-item-content>
                      </template>
                    </v-autocomplete>
                  </v-col>
                  <v-col cols="12">
                    <v-select
                      v-model="filter.has_special_permissions"
                      :items="permissionFilterOptions"
                      item-text="name"
                      item-value="value"
                      label="Permisos especiales"
                      outlined
                      clearable
                      hide-details="auto"
                      prepend-inner-icon="mdi-shield-star"
                    >
                      <template v-slot:selection="{ item }">
                        <v-icon left small :color="item.value ? 'success' : 'grey'">
                          {{ item.value ? 'mdi-shield-check' : 'mdi-shield-off' }}
                        </v-icon>
                        {{ item.name }}
                      </template>
                      <template v-slot:item="{ item }">
                        <v-icon left :color="item.value ? 'success' : 'grey'">
                          {{ item.value ? 'mdi-shield-check' : 'mdi-shield-off' }}
                        </v-icon>
                        {{ item.name }}
                      </template>
                    </v-select>
                  </v-col>
                </v-row>
              </filter-menu>

              <!-- Botón para configurar permisos por dealer -->
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn icon @click="openPermissionsDialog" v-on="on" color="white">
                    <v-icon>mdi-plus</v-icon>
                  </v-btn>
                </template>
                <span>Configurar permisos por dealer</span>
              </v-tooltip>
              
              <!-- Búsqueda para móvil -->
              <v-tooltip top v-if="$vuetify.breakpoint.mdAndDown && !showSearch">
                <template v-slot:activator="{ on }">
                  <v-btn icon v-on="on" @click.stop="toggleSearch">
                    <v-icon>mdi-magnify</v-icon>
                  </v-btn>
                </template>
                <span>Buscar</span>
              </v-tooltip>
              
              <v-col cols="12" v-if="showSearch && $vuetify.breakpoint.mdAndDown">
                <v-text-field
                  ref="searchInput"
                  v-model="search"
                  label="Buscar usuario..."
                  prepend-inner-icon="mdi-magnify"
                  hide-details
                  clearable
                  rounded
                  single-line
                  filled
                  dense
                  dark
                  @keydown.esc="showSearch = false"
                ></v-text-field>
              </v-col>
              
              <!-- Botón para refrescar -->
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn icon @click="loadUsers" v-on="on" color="white">
                    <v-icon>mdi-refresh</v-icon>
                  </v-btn>
                </template>
                <span>Actualizar lista</span>
              </v-tooltip>
            </v-toolbar>

            <!-- Filtros rápidos bajo la barra de herramientas -->
            <v-card-text class="py-2 px-4">
              <v-row class="align-center justify-center">
                <v-col cols="auto">
                  <v-chip-group v-model="quickFilter" @change="applyQuickFilter" class="py-1">
                    <v-chip filter outlined color="green" small class="mr-1">
                      <v-icon left x-small>mdi-account-check</v-icon>
                      Habilitados
                    </v-chip>
                    <v-chip filter outlined color="red" small class="mr-1">
                      <v-icon left x-small>mdi-account-off</v-icon>
                      Deshabilitados
                    </v-chip>
                  </v-chip-group>
                </v-col>

                <v-col cols="auto" v-if="hasActiveFilters">
                  <v-btn x-small text color="info" @click="resetFilters" class="ml-2">
                    <v-icon left small>mdi-filter-remove</v-icon>
                    Limpiar filtros
                  </v-btn>
                </v-col>

                <v-col cols="auto" class="ml-auto">
                  <div class="text-subtitle-1 font-weight-medium grey--text text--darken-2">
                    {{ filteredUsers.length }} usuario(s)
                  </div>
                </v-col>
                
                <v-col cols="auto">
                  <v-btn-toggle v-model="viewMode" mandatory dense color="info">
                    <v-btn value="cards" small>
                      <v-icon small>mdi-view-grid-outline</v-icon>
                      Tarjetas
                    </v-btn>
                    <v-btn value="table" small>
                      <v-icon small>mdi-table</v-icon>
                      Tabla
                    </v-btn>
                  </v-btn-toggle>
                </v-col>
              </v-row>

              <!-- Filtros aplicados -->
              <v-row v-if="hasActiveFilters" class="mt-1 justify-center">
                <v-col cols="12">
                  <div class="d-flex align-center flex-wrap justify-center">
                    <span class="text-caption mr-2 grey--text">Filtros aplicados:</span>
                    <v-chip v-if="filter.roles" x-small class="mr-1 mb-1" close color="info" text-color="white"
                      @click:close="filter.roles = null; applyFilters()">
                      Rol: {{ getRoleName(filter.roles) }}
                    </v-chip>
                    <v-chip v-if="filter.status !== null && filter.status !== undefined" x-small class="mr-1 mb-1" close
                      :color="filter.status === 10 ? 'green' : 'red'" text-color="white"
                      @click:close="filter.status = null; applyFilters()">
                      {{ filter.status === 10 ? 'Habilitado' : 'Deshabilitado' }}
                    </v-chip>
                    <v-chip v-if="filter.dealership_id" x-small class="mr-1 mb-1" close color="info" text-color="white"
                      @click:close="filter.dealership_id = null; applyFilters()">
                      Dealer: {{ getDealerName(filter.dealership_id) }}
                    </v-chip>
                    <v-chip v-if="filter.has_special_permissions !== null && filter.has_special_permissions !== undefined" 
                      x-small class="mr-1 mb-1" close 
                      :color="filter.has_special_permissions ? 'success' : 'grey'" 
                      text-color="white"
                      @click:close="filter.has_special_permissions = null; applyFilters()">
                      <v-icon left x-small>
                        {{ filter.has_special_permissions ? 'mdi-shield-check' : 'mdi-shield-off' }}
                      </v-icon>
                      {{ filter.has_special_permissions ? 'Con permisos especiales' : 'Sin permisos especiales' }}
                    </v-chip>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>

            <v-divider></v-divider>

            <!-- Estado de carga -->
            <div v-if="loading" class="pa-3">
              <!-- Skeleton loaders para la vista de tabla -->
              <template v-if="viewMode === 'table'">
                <v-skeleton-loader type="table-heading" class="mb-2"></v-skeleton-loader>
                <v-skeleton-loader type="table-row@6"></v-skeleton-loader>
              </template>

              <!-- Skeleton loaders para vista de tarjetas -->
              <v-row v-else>
                <v-col cols="12" sm="6" md="4" lg="3" v-for="n in 8" :key="`skeleton-${n}`">
                  <v-skeleton-loader type="card" class="mb-4"></v-skeleton-loader>
                </v-col>
              </v-row>
            </div>

            <!-- Vista de tabla -->
            <v-card-text v-else-if="viewMode === 'table'" class="pa-0">
              <v-data-table
                :headers="headers"
                :items="filteredUsers"
                :search="search"
                :items-per-page="15"
                :item-key="'unique_key'"
                class="elevation-0"
                sort-by="dealership_name"
              >
                <!-- Columna de usuario -->
                <template v-slot:item.user="{ item }">
                  <div class="d-flex align-center py-2">
                    <v-avatar size="40" color="primary lighten-4" class="mr-3">
                      <v-icon color="primary">mdi-account</v-icon>
                    </v-avatar>
                    <div>
                      <div class="font-weight-medium">{{ item.name }} {{ item.lastname }}</div>
                      <div class="text-caption grey--text">{{ item.username }}</div>
                    </div>
                  </div>
                </template>

                <!-- Columna de dealer -->
                <template v-slot:item.dealership_name="{ item }">
                  <div class="d-flex align-center">
                    <v-icon small color="primary" class="mr-2">mdi-store</v-icon>
                    <div>
                      <div class="font-weight-medium">{{ item.dealership_name }}</div>
                      <v-chip
                        x-small
                        :color="item.subscription_status === 'active' ? 'success' : 'warning'"
                        text-color="white"
                        class="mt-1"
                      >
                        {{ item.subscription_status || 'N/A' }}
                      </v-chip>
                    </div>
                  </div>
                </template>

                <!-- Columna de email -->
                <template v-slot:item.email="{ item }">
                  <div class="text-body-2">
                    {{ item.email }}
                  </div>
                </template>

                <!-- Columna de teléfono -->
                <template v-slot:item.phone="{ item }">
                  <div class="text-body-2" v-if="item.phone">
                    <v-icon x-small class="mr-1">mdi-phone</v-icon>
                    {{ item.phone }}
                  </div>
                  <span v-else class="grey--text font-italic">Sin teléfono</span>
                </template>

                <!-- Columna de roles -->
                <template v-slot:item.roles="{ item }">
                  <div class="d-flex flex-wrap gap-1">
                    <v-chip v-for="(rol, index) in item.roles" :key="index"
                        x-small outlined class="mr-1 mb-1" color="primary">
                        {{ getRoleName(rol) }}
                    </v-chip>
                  </div>
                </template>

                <!-- Columna de estado -->
                <template v-slot:item.status="{ item }">
                  <v-chip :color="getColor(item.status)" small dark>
                        <v-icon left x-small>
                        {{ item.status == 10 ? "mdi-check-circle" : "mdi-block-helper" }}
                        </v-icon>
                        {{ item.status == 10 ? "Habilitado" : "Deshabilitado" }}
                    </v-chip>
                </template>

                <!-- Estado sin resultados -->
                <template v-slot:no-data>
                  <div class="text-center py-5">
                    <v-icon size="80" color="grey lighten-2" class="mb-4">mdi-account-off</v-icon>
                    <h3 class="text-h6 grey--text text--darken-1">No se encontraron usuarios</h3>
                  </div>
                </template>
              </v-data-table>
            </v-card-text>

            <!-- Vista de tarjetas -->
            <v-card-text v-else class="pa-3">
              <!-- Sin resultados -->
              <v-row v-if="!filteredUsers.length" class="text-center py-5">
                <v-col cols="12">
                  <v-icon size="80" color="grey lighten-2" class="mb-4">mdi-account-off</v-icon>
                  <h3 class="text-h5 grey--text text--darken-1 mb-2">No se encontraron usuarios</h3>
                  <p class="text-subtitle-1 grey--text mb-6">Este grupo no tiene usuarios asignados</p>
                </v-col>
              </v-row>

              <!-- Lista de tarjetas -->
              <v-row v-else>
                <v-col cols="12" sm="6" md="4" lg="3" v-for="item in filteredUsers" :key="item.unique_key">
                  <v-card outlined hover class="user-item-card h-100">
                    <v-card-text class="pa-4">
                      <!-- Header de tarjeta con estado -->
                      <div class="d-flex justify-space-between align-center mb-3">
                        <v-chip :color="getColor(item.status)" small dark>
                          <v-icon left x-small>
                            {{ item.status == 10 ? "mdi-check-circle" : "mdi-block-helper" }}
                          </v-icon>
                          {{ item.status == 10 ? "Habilitado" : "Deshabilitado" }}
                        </v-chip>
                      </div>

                      <!-- Avatar y nombre -->
                      <div class="d-flex align-center mb-3">
                        <v-avatar size="56" color="primary lighten-4" class="mr-3">
                          <v-icon size="32" color="primary">mdi-account</v-icon>
                        </v-avatar>
                        <div class="flex-grow-1">
                          <div class="subtitle-1 font-weight-bold mb-1">
                            {{ item.name }} {{ item.lastname }}
                          </div>
                          <div class="text-caption grey--text">
                            {{ item.email }}
                          </div>
                        </div>
                      </div>

                      <!-- Información del dealer -->
                      <div class="mb-2">
                        <v-icon x-small class="mr-1">mdi-store</v-icon>
                        <span class="text-caption font-weight-medium">{{ item.dealership_name }}</span>
                        <v-chip
                          x-small
                          :color="item.subscription_status === 'active' ? 'success' : 'warning'"
                          text-color="white"
                          class="ml-2"
                        >
                          {{ item.subscription_status || 'N/A' }}
                        </v-chip>
                      </div>

                      <!-- Teléfono -->
                      <div class="mb-2" v-if="item.phone">
                        <v-icon x-small class="mr-1">mdi-phone</v-icon>
                        <span class="text-caption">{{ item.phone }}</span>
                      </div>

                      <!-- Roles -->
                      <div class="mt-3">
                        <div class="text-caption grey--text mb-1">Roles:</div>
                        <div class="d-flex flex-wrap gap-1">
                          <v-chip 
                            v-for="(rol, index) in item.roles" 
                            :key="index"
                            x-small 
                            outlined 
                            class="mr-1 mb-1" 
                            color="primary"
                          >
                            {{ getRoleName(rol) }}
                          </v-chip>
                        </div>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Diálogo de configuración de permisos por dealer -->
    <user-permissions-dialog
      v-if="permissionsDialog"
      :users="allUsers"
      :dealers="groupDealerships"
      @save="handleSavePermissions"
      @cancel="permissionsDialog = false"
    />
  </div>
</template>

<script>
import PartnerGroupService from '@/services/PartnerGroupService'
import FilterMenu from '@/components/FilterMenu'
import UserPermissionsDialog from '@/components/UserPermissionsDialog'
import UserService from '@/services/UserService'

const STATUS_ENABLED = 10

export default {
  name: 'PartnerGroupUsers',
  components: {
    FilterMenu,
    UserPermissionsDialog
  },
  data() {
    return {
      groupId: null,
      groupName: '',
      users: [],
      allUsers: [], // Todos los usuarios sin filtrar
      groupDealerships: [], // Lista de dealers del grupo
      loading: false,
      search: '',
      showSearch: false,
      manualViewMode: null,
      quickFilter: null,
      
      // Diálogo de permisos
      permissionsDialog: false,
      
      // Filtros
      filter: {
        roles: null,
        status: null,
        dealership_id: null,
        has_special_permissions: null
      },
      headers: [
        {
          text: 'Usuario',
          value: 'email',
          sortable: true,
          width: '25%'
        },
        {
          text: 'Nombre y Apellidos',
          value: 'user',
          sortable: true,
          width: '20%'
        },
        {
          text: 'Concesionario',
          value: 'dealership_name',
          sortable: true,
          width: '20%'
        },
        {
          text: 'Teléfono',
          value: 'phone',
          sortable: false,
          width: '15%'
        },
        {
          text: 'Roles',
          value: 'roles',
          sortable: false,
          width: '15%'
        },
        {
          text: 'Estado',
          value: 'status',
          sortable: true,
          align: 'center',
          width: '5%'
        },
        {
          text: 'Acciones',
          value: 'action',
          sortable: false,
          align: 'center',
          width: '13%'
        }
      ],
        roleDisplay: {
        admin: 'Administrador',
        logistics: 'Logística',
        manager: 'Manager',
        commercial: 'Commercial',
        sales: 'Jefe de ventas/taller',
        // driver: 'Conductor',
        // garage: 'Garage',
        mechanic: 'Mecánico'
        // cleaning: 'Limpieza'
        },
      
      estados: [
        { name: 'Habilitado', value: STATUS_ENABLED },
        { name: 'Deshabilitado', value: 0 }
      ],
      
      permissionFilterOptions: [
        { name: 'Con permisos especiales', value: true },
        { name: 'Sin permisos especiales', value: false }
      ],
      
      availableRoles: [
        { name: 'Administrador', value: 'admin' },
        { name: 'Logística', value: 'logistics' },
        { name: 'Manager', value: 'manager' },
        { name: 'Commercial', value: 'commercial' },
        { name: 'Jefe de ventas/taller', value: 'sales' },
        { name: 'Mecánico', value: 'mechanic' }
      ]
    }
  },
  computed: {
    viewMode: {
      get() {
        if (this.manualViewMode) {
          return this.manualViewMode
        }
        // Vista de tabla por defecto en escritorio, tarjetas en móvil
        if (this.$vuetify && this.$vuetify.breakpoint && this.$vuetify.breakpoint.mdAndUp) {
          return 'table'
        }
        return 'cards'
      },
      set(value) {
        this.manualViewMode = value
      }
    },
    
    filteredUsers() {
      let filtered = [...this.allUsers]
      
      // Filtro por rol
      if (this.filter.roles) {
        filtered = filtered.filter(user => user.roles && user.roles.includes(this.filter.roles))
      }
      
      // Filtro por estado
      if (this.filter.status !== null && this.filter.status !== undefined) {
        filtered = filtered.filter(user => user.status === this.filter.status)
      }
      
      // Filtro por dealer
      if (this.filter.dealership_id) {
        filtered = filtered.filter(user => user.dealership_id === this.filter.dealership_id)
      }
      
      // Filtro por permisos especiales
      if (this.filter.has_special_permissions !== null && this.filter.has_special_permissions !== undefined) {
        filtered = filtered.filter(user => {
          const hasSpecialPermissions = user.partnerPermissions && Object.keys(user.partnerPermissions).length > 0
          return this.filter.has_special_permissions ? hasSpecialPermissions : !hasSpecialPermissions
        })
      }
      
      // Filtro de búsqueda
      if (this.search) {
        const searchLower = this.search.toLowerCase()
        filtered = filtered.filter(user =>
          (user.name && user.name.toLowerCase().includes(searchLower)) ||
          (user.lastname && user.lastname.toLowerCase().includes(searchLower)) ||
          (user.email && user.email.toLowerCase().includes(searchLower)) ||
          (user.username && user.username.toLowerCase().includes(searchLower)) ||
          (user.dealership_name && user.dealership_name.toLowerCase().includes(searchLower))
        )
      }
      
      return filtered
    },
    
    totalUsers() {
      return this.allUsers.length
    },
    
    hasActiveFilters() {
      return this.filter.roles !== null || 
             this.filter.status !== null || 
             this.filter.dealership_id !== null ||
             this.filter.has_special_permissions !== null ||
             this.quickFilter !== null
    }
  },
  mounted() {
    this.groupId = this.$route.params.groupId
    this.loadUsers()
  },
  methods: {
    async loadUsers() {
      this.loading = true
      try {
        // Cargar el grupo con sus dealers y usuarios en paralelo
        const [groupResponse, usersResponse] = await Promise.all([
          PartnerGroupService.view(this.groupId, {
            expand: 'dealerships.balance'
          }),
          PartnerGroupService.getUsers(this.groupId, {
            expand: 'partnerPermissions'
          })
        ])
        
        const group = groupResponse.data.data || groupResponse.data
        
        // Actualizar el nombre del grupo
        this.groupName = group.name || 'Sin nombre'
        
        // Guardar la lista de dealers del grupo para los filtros
        this.groupDealerships = group.dealerships || []
        
        // Obtener los usuarios del endpoint
        const groupUsers = usersResponse.data.data || usersResponse.data || []
        
        // Inicializar array de usuarios
        this.users = []
        this.allUsers = []
        
        // Agregar cada usuario con información del dealer
        groupUsers.forEach(user => {
          // Buscar información completa del dealer desde groupDealerships
          const userDealerId = user.dealer?.id || user.dealer_id
          const userDealer = this.groupDealerships.find(d => d.id === userDealerId)
          
          const userData = {
            ...user,
            unique_key: `${userDealerId}-${user.id}`, // Clave única combinando dealer_id y user_id
            dealership_id: userDealerId,
            dealership_name: user.dealer?.name || userDealer?.name || 'N/A',
            subscription_status: userDealer?.balance?.subscription_status
          }
          this.users.push(userData)
          this.allUsers.push(userData)
        })
        
        if (this.users.length === 0) {
          this.$toast.info('No se encontraron usuarios en los dealers del grupo')
        }
      } catch (error) {
        console.error('Error al cargar datos del grupo:', error)
        console.error('Detalles del error:', error.response)
        this.$toast.error('Error al cargar los datos del grupo')
      } finally {
        this.loading = false
      }
    },
    
    goBack() {
      this.$router.push({ name: 'Partner' })
    },

    toggleSearch() {
      this.showSearch = !this.showSearch
      if (this.showSearch) {
        this.$nextTick(() => {
          if (this.$refs.searchInput) {
            this.$refs.searchInput.focus()
          }
        })
      }
    },

    applyQuickFilter() {
      if (this.quickFilter === 0) {
        // Filtro Habilitados
        this.filter.status = STATUS_ENABLED
      } else if (this.quickFilter === 1) {
        // Filtro Deshabilitados
        this.filter.status = 0
      } else {
        this.filter.status = null
      }
    },

    applyFilters() {
      // Los filtros se aplican automáticamente a través del computed filteredUsers
    },

    resetFilters() {
      this.filter = {
        roles: null,
        status: null,
        dealership_id: null,
        has_special_permissions: null
      }
      this.quickFilter = null
      this.search = ''
    },

    getDealerName(dealerId) {
      const dealer = this.groupDealerships.find(d => d.id === dealerId)
      return dealer ? dealer.name : 'Desconocido'
    },

    getRoleName (rol) {
      return this.roleDisplay[rol] || rol.charAt(0).toUpperCase() + rol.slice(1)
    },

    getColor (estado) {
      return (estado === STATUS_ENABLED ? `green` : `red`)
    },

    // Métodos para el diálogo de configuración de permisos
    openPermissionsDialog() {
      this.permissionsDialog = true
    },

    async handleSavePermissions(permissionsData) {
      try {
        // NO filtrar dealers con permisos vacíos, ya que un array vacío significa eliminar todos los permisos
        const dealersToUpdate = permissionsData.dealer_permissions

        if (dealersToUpdate.length === 0) {
          this.$toast.warning('No hay cambios de permisos para guardar')
          return
        }

        // Una petición por cada dealer (incluso si tiene permisos vacíos, para eliminarlos)
        const promises = dealersToUpdate.map(dealerPerm => {
          return UserService.setUserDealershipPermissions({
            user_id: permissionsData.user_id,
            dealership_id: dealerPerm.dealer_id,
            permissions: dealerPerm.permissions // Puede ser un array vacío para eliminar todos
          })
        })

        await Promise.all(promises)

        const totalPermisos = dealersToUpdate.reduce(
          (sum, dp) => sum + dp.permissions.length, 0
        )

        // Mensaje personalizado según si se agregaron o eliminaron permisos
        if (totalPermisos === 0) {
          this.$toast.success(
            `Permisos eliminados de ${dealersToUpdate.length} dealer(s)`
          )
        } else {
          this.$toast.success(
            `Permisos guardados en ${dealersToUpdate.length} dealer(s) — ${totalPermisos} permiso(s) en total`
          )
        }

        this.permissionsDialog = false
        await this.loadUsers()
      } catch (error) {
        console.error('Error al guardar permisos:', error)
        this.$toast.error('Error al guardar los permisos del usuario')
      }
    }
  }
}
</script>

<style scoped>
.gap-1 {
  gap: 4px;
}

.user-item-card {
  transition: box-shadow 0.2s ease-in-out, transform 0.2s ease;
  display: flex;
  flex-direction: column;
}

.user-item-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
  transform: translateY(-2px);
}

.h-100 {
  height: 100%;
}

.flex-grow-1 {
  flex-grow: 1;
}
</style>
